import {
  arrayUnion,
  doc,
  increment,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import db from '../firebase/firebaseConfig';

const SHORTLINK_METRICS_COLLECTION = 'shortlinkMetrics';
const SHORTLINK_VISITORS_COLLECTION = 'shortlinkVisitors';
const VISITOR_GEO_CACHE_KEY = 'hcva_shortlink_geo';
const VISITOR_SESSION_ID_KEY = 'hcva_visitor_sid';
const VISITOR_GEO_CACHE_TTL_MS = 60 * 1000;

function getVisitorSessionId() {
  try {
    let id = sessionStorage.getItem(VISITOR_SESSION_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem(VISITOR_SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function trackedVisitorSessionKey(siteId, visitorSessionId) {
  return `hcva_shortlink_fs_first_${siteId}_${visitorSessionId}`;
}

export function hasTrackedShortlinkVisitorSession(siteId) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return false;
  try {
    const visitorSessionId = getVisitorSessionId();
    return sessionStorage.getItem(
      trackedVisitorSessionKey(normalizedSiteId, visitorSessionId)
    ) === '1';
  } catch {
    return false;
  }
}

/** sessionStorage: set before navigating to shortlink — consumed once on embed load. */
export const SHORTLINK_VISIT_NOTE_KEY = 'hcva_shortlink_visit_note';
export const SHORTLINK_VISIT_FROM_POPUP = 'popup';
export const SHORTLINK_VISIT_FROM_REDIRECT_GATE = 'redirect_gate';

/** Human-readable note for why geo/device row was recorded (embed vs fullscreen). */
export function consumePendingVisitNoteForEmbed() {
  try {
    const v = sessionStorage.getItem(SHORTLINK_VISIT_NOTE_KEY);
    sessionStorage.removeItem(SHORTLINK_VISIT_NOTE_KEY);
    if (v === SHORTLINK_VISIT_FROM_POPUP) {
      return 'Popup screen visit opened the shortlink offer.';
    }
    if (v === SHORTLINK_VISIT_FROM_REDIRECT_GATE) {
      return 'Redirect gate click opened the shortlink offer screen.';
    }
  } catch {
    /* ignore */
  }
  return 'Shortlink offer screen loaded.';
}

export const VISIT_NOTE_FULLSCREEN = 'Entered fullscreen.';

export const VISIT_NOTE_SECURITY_CHECK =
  'Security check screen shown (redirect screen 1 — IP).';

export const VISIT_NOTE_OFFER_SCREEN =
  'Offer page shown (redirect screen 2).';

export const VISIT_NOTE_REDIRECT_RECLICK =
  'Mouse click while on shortlink (redirect gate).';

/** Log-only lines (no visitCount increment) — fullscreen exit, tab close, etc. */
export const VISIT_NOTE_FULLSCREEN_EXIT = 'Exited fullscreen.';
export const VISIT_NOTE_TAB_OR_PAGE_CLOSED =
  'Page unload / left signal detected (tab close, refresh, navigation, or browser left page).';

/** Readable labels for how fullscreen was exited (mirrors clickTracking close ways). */
const FS_EXIT_METHOD_LABELS = {
  esc_button: 'Esc key',
  f11_key: 'F11 key',
  back_button: 'Back button',
  tab_close: 'Tab close / reload',
  'browser/system': 'Browser / system',
};

/** "Exited fullscreen — Esc key." */
export function fullscreenExitNote(method) {
  const label = FS_EXIT_METHOD_LABELS[method] || 'Browser / system';
  return `Exited fullscreen — ${label}.`;
}

/** "Key press: Escape." (display-only key name). */
export function keyPressNote(key, { ctrl, alt, shift, meta } = {}) {
  const named = key === ' ' ? 'Space' : key || 'Unknown';
  const mods = [ctrl && 'Ctrl', alt && 'Alt', shift && 'Shift', meta && 'Meta'].filter(Boolean);
  return `Status: key pressed — Key: ${mods.length ? `${mods.join('+')}+` : ''}${named}.`;
}

/** "Fullscreen close attempt — key pressed: F11." */
export function fullscreenKeyAttemptNote(key, { ctrl, alt, shift, meta } = {}) {
  const named = key === ' ' ? 'Space' : key || 'Unknown';
  const mods = [ctrl && 'Ctrl', alt && 'Alt', shift && 'Shift', meta && 'Meta'].filter(Boolean);
  return `Fullscreen close attempt — status: key pressed — Key: ${mods.length ? `${mods.join('+')}+` : ''}${named}.`;
}

/** Label + CSS-ish detail for any click target (offer iframe + parent). */
export function describeClickTarget(target) {
  try {
    if (!target || target.nodeType !== 1) {
      return { label: '', detail: '' };
    }
    const el =
      target.closest?.(
        'button, a, [role="button"], input, select, textarea, label, summary, [onclick], [data-action]'
      ) || target;

    const tag = String(el.tagName || 'el').toLowerCase();
    let label = '';
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement
    ) {
      label = (
        el.getAttribute('aria-label') ||
        el.getAttribute('placeholder') ||
        el.name ||
        el.id ||
        el.value ||
        el.type ||
        ''
      )
        .trim()
        .slice(0, 48);
    } else {
      label = (
        el.getAttribute?.('aria-label') ||
        el.getAttribute?.('title') ||
        el.innerText ||
        el.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 48);
    }

    const id = el.id ? `#${el.id}` : '';
    let cls = '';
    if (typeof el.className === 'string' && el.className.trim()) {
      cls =
        '.' +
        el.className
          .trim()
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .join('.');
    }
    const detail = `${tag}${id}${cls}`.slice(0, 64);
    return { label, detail };
  } catch {
    return { label: '', detail: '' };
  }
}

/**
 * Visit-log line for any click.
 * Examples:
 *   Element click: "Enter" <button.cta> at (120, 340).
 *   Element click: <div.card> on offer at (40, 80).
 */
export function clickNote({ label, x, y, detail, inOffer } = {}) {
  const where = x != null && y != null ? ` at (${x}, ${y})` : '';
  const scope = inOffer ? ' on offer' : '';
  const cleanLabel = label ? String(label).replace(/\s+/g, ' ').trim().slice(0, 48) : '';
  const cleanDetail = detail ? String(detail).replace(/\s+/g, '').slice(0, 64) : '';

  if (cleanLabel && /^(button|a|input)/i.test(cleanDetail || 'button')) {
    return `Button click: "${cleanLabel}"${where}.`;
  }
  if (cleanLabel) {
    return `Element click: "${cleanLabel}"${cleanDetail ? ` <${cleanDetail}>` : ''}${scope}${where}.`;
  }
  if (cleanDetail) {
    return `Element click: <${cleanDetail}>${scope}${where}.`;
  }
  return `Element click${scope}${where}.`;
}

export function clickNoteFromEvent(e, { inOffer = false } = {}) {
  const { label, detail } = describeClickTarget(e?.target);
  return clickNote({
    label,
    detail,
    inOffer,
    x: Math.round(e?.clientX || 0),
    y: Math.round(e?.clientY || 0),
  });
}

function getDeviceType() {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (/ipad|tablet|playbook|silk/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|iemobile|blackberry|opera mini/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/** Human-readable OS for admin (macOS / Windows / Linux / iOS / Android / Other). */
export function parseOsLabel(ua, platform) {
  const u = (ua || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(u)) return 'iOS';
  if (/android/.test(u)) return 'Android';
  if (/macintosh|mac os x/.test(u)) return 'macOS';
  if (/windows/.test(u)) return 'Windows';
  if (/linux/.test(u)) return 'Linux';
  const p = (platform || '').toLowerCase();
  if (p.includes('mac')) return 'macOS';
  if (p.includes('win')) return 'Windows';
  return 'Other';
}

/** Browser name from user-agent string. */
export function parseBrowserName(ua) {
  const u = ua || '';
  if (/Edg\//i.test(u)) return 'Edge';
  if (/OPR\/|Opera\/|Opera Mini/i.test(u)) return 'Opera';
  if (/Firefox\//i.test(u)) return 'Firefox';
  if (/Chrome\/|CriOS/i.test(u)) return 'Chrome';
  if (/Safari/i.test(u) && !/Chrome/i.test(u)) return 'Safari';
  return 'Other';
}

function emptyVisitorGeo() {
  return {
    ip: '',
    countryName: '',
    countryCode: '',
    city: '',
    region: '',
    isp: '',
    geoTimezone: '',
  };
}

function captureLandingContext() {
  const out = {
    landingPath: '',
    landingUrl: '',
    referrer: '',
    search: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmTerm: '',
    utmContent: '',
    gclid: '',
    fbclid: '',
    heatmapVisitorId: '',
    timezone: '',
    screenW: 0,
    screenH: 0,
    viewportW: 0,
    viewportH: 0,
    devicePixelRatio: 1,
  };
  try {
    const url = new URL(window.location.href);
    out.landingPath = url.pathname || '';
    out.landingUrl = String(url.href || '').slice(0, 500);
    out.search = String(url.search || '').slice(0, 300);
    out.referrer = String(document.referrer || '').slice(0, 500);
    const p = url.searchParams;
    out.utmSource = String(p.get('utm_source') || '').slice(0, 120);
    out.utmMedium = String(p.get('utm_medium') || '').slice(0, 120);
    out.utmCampaign = String(p.get('utm_campaign') || '').slice(0, 120);
    out.utmTerm = String(p.get('utm_term') || '').slice(0, 120);
    out.utmContent = String(p.get('utm_content') || '').slice(0, 120);
    out.gclid = String(p.get('gclid') || '').slice(0, 120);
    out.fbclid = String(p.get('fbclid') || '').slice(0, 120);
  } catch {
    /* ignore */
  }
  try {
    out.heatmapVisitorId = localStorage.getItem('hcva_visitor_id') || '';
  } catch {
    /* ignore */
  }
  try {
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    /* ignore */
  }
  try {
    out.screenW = window.screen?.width || 0;
    out.screenH = window.screen?.height || 0;
    out.viewportW = window.innerWidth || 0;
    out.viewportH = window.innerHeight || 0;
    out.devicePixelRatio = window.devicePixelRatio || 1;
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * IP + country + city/ISP (HTTPS, browser-safe). Cached briefly.
 * Primary: ipapi.co; fallback: ipify + ipwho.is.
 */
async function resolveVisitorGeo() {
  try {
    const raw = sessionStorage.getItem(VISITOR_GEO_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed &&
        typeof parsed.ip === 'string' &&
        (!parsed.cachedAt || Date.now() - parsed.cachedAt <= VISITOR_GEO_CACHE_TTL_MS)
      ) {
        return { ...emptyVisitorGeo(), ...parsed };
      }
    }
  } catch {
    /* ignore */
  }

  const empty = emptyVisitorGeo();

  try {
    const res = await fetch(`https://ipapi.co/json/?ts=${Date.now()}`, { cache: 'no-store' });
    if (res.ok) {
      const d = await res.json();
      if (!d || d.error) throw new Error(d?.reason || 'ipapi error');
      const ip = typeof d.ip === 'string' ? d.ip : '';
      const countryName =
        typeof d.country_name === 'string'
          ? d.country_name
          : typeof d.country === 'string' && d.country.length > 2
            ? d.country
            : '';
      let countryCode =
        typeof d.country_code === 'string'
          ? d.country_code
          : typeof d.country === 'string' && d.country.length === 2
            ? d.country
            : '';
      countryCode = (countryCode || '').toUpperCase().slice(0, 2);
      const out = {
        ...empty,
        ip,
        countryName: countryName || '',
        countryCode,
        city: typeof d.city === 'string' ? d.city : '',
        region: typeof d.region === 'string' ? d.region : '',
        isp: typeof d.org === 'string' ? d.org : '',
        geoTimezone: typeof d.timezone === 'string' ? d.timezone : '',
      };
      if (out.ip) {
        try {
          sessionStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify({ ...out, cachedAt: Date.now() }));
        } catch {
          /* ignore */
        }
        return out;
      }
    }
  } catch {
    /* try fallbacks */
  }

  try {
    const res = await fetch(`https://api64.ipify.org?format=json&ts=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return empty;
    const data = await res.json();
    const ip = typeof data?.ip === 'string' ? data.ip : '';
    if (!ip) return empty;

    const out = { ...empty, ip };
    try {
      const g = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`, { cache: 'no-store' });
      if (g.ok) {
        const w = await g.json();
        if (w.success !== false) {
          out.countryName = typeof w.country === 'string' ? w.country : '';
          out.countryCode =
            typeof w.country_code === 'string'
              ? w.country_code.toUpperCase().slice(0, 2)
              : '';
          out.city = typeof w.city === 'string' ? w.city : '';
          out.region = typeof w.region === 'string' ? w.region : '';
          out.isp =
            (typeof w.connection?.isp === 'string' && w.connection.isp) ||
            (typeof w.connection?.org === 'string' && w.connection.org) ||
            '';
          out.geoTimezone = typeof w.timezone?.id === 'string' ? w.timezone.id : '';
        }
      }
    } catch {
      /* country unknown */
    }

    try {
      sessionStorage.setItem(VISITOR_GEO_CACHE_KEY, JSON.stringify({ ...out, cachedAt: Date.now() }));
    } catch {
      /* ignore */
    }
    return out;
  } catch {
    return empty;
  }
}

export async function trackShortlinkFullscreenTrigger({
  siteId,
  slug,
  sourceDomain,
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  try {
    await setDoc(
      doc(db, SHORTLINK_METRICS_COLLECTION, normalizedSiteId),
      {
        siteId: normalizedSiteId,
        fullscreenTriggers: increment(1),
        lastSlug: slug || '',
        lastSourceDomain: sourceDomain || '',
        lastFullscreenAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error('[shortlinkMetrics] fullscreen trigger write failed:', err);
  }
}

export async function trackShortlinkVisitor({
  siteId,
  slug,
  sourceDomain,
  visitNote = '',
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  const geo = await resolveVisitorGeo();
  const landing = captureLandingContext();
  const ua = navigator.userAgent || '';
  const language = navigator.language || '';
  const platform = navigator.platform || '';
  const osLabel = parseOsLabel(ua, platform);
  const browserName = parseBrowserName(ua);
  const visitorSessionId = getVisitorSessionId();
  const sessionRef = doc(
    db,
    SHORTLINK_VISITORS_COLLECTION,
    normalizedSiteId,
    'sessions',
    visitorSessionId
  );

  const iso = new Date().toISOString();
  const logLine = `${iso}|${visitNote || '—'}`;

  const firstDocKey = trackedVisitorSessionKey(normalizedSiteId, visitorSessionId);
  let isFirstWriteToSessionDoc = false;
  try {
    isFirstWriteToSessionDoc = !sessionStorage.getItem(firstDocKey);
  } catch {
    isFirstWriteToSessionDoc = true;
  }

  const payload = {
    visitorSessionId,
    siteId: normalizedSiteId,
    slug: slug || '',
    lastSlug: slug || '',
    sourceDomain: sourceDomain || '',
    ipAddress: geo.ip || '',
    countryName: geo.countryName || '',
    countryCode: geo.countryCode || '',
    city: geo.city || '',
    region: geo.region || '',
    isp: geo.isp || '',
    geoTimezone: geo.geoTimezone || '',
    deviceType: getDeviceType(),
    osLabel,
    browserName,
    userAgent: ua,
    language,
    platform,
    timezone: landing.timezone || geo.geoTimezone || '',
    screenW: landing.screenW || 0,
    screenH: landing.screenH || 0,
    viewportW: landing.viewportW || 0,
    viewportH: landing.viewportH || 0,
    devicePixelRatio: landing.devicePixelRatio || 1,
    lastVisitNote: visitNote || '',
    lastVisitedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    visitLog: arrayUnion(logLine),
    visitCount: increment(1),
  };

  if (isFirstWriteToSessionDoc) {
    payload.firstVisitedAt = serverTimestamp();
    payload.landingPath = landing.landingPath || '';
    payload.landingUrl = landing.landingUrl || '';
    payload.referrer = landing.referrer || '';
    payload.search = landing.search || '';
    payload.utmSource = landing.utmSource || '';
    payload.utmMedium = landing.utmMedium || '';
    payload.utmCampaign = landing.utmCampaign || '';
    payload.utmTerm = landing.utmTerm || '';
    payload.utmContent = landing.utmContent || '';
    payload.gclid = landing.gclid || '';
    payload.fbclid = landing.fbclid || '';
    payload.heatmapVisitorId = landing.heatmapVisitorId || '';
  }

  try {
    await setDoc(sessionRef, payload, { merge: true });
    try {
      sessionStorage.setItem(firstDocKey, '1');
    } catch {
      /* ignore */
    }
  } catch (err) {
    console.error('[shortlinkMetrics] visitor session write failed:', err);
  }
}

/**
 * Append one line to visitLog without incrementing visitCount (for exit / unload events).
 */
export function appendShortlinkVisitLogNote({
  siteId,
  slug,
  sourceDomain,
  visitNote = '',
  requireExistingSession = false,
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId || !visitNote) return;

  const visitorSessionId = getVisitorSessionId();
  if (requireExistingSession && !hasTrackedShortlinkVisitorSession(normalizedSiteId)) {
    return;
  }
  const sessionRef = doc(
    db,
    SHORTLINK_VISITORS_COLLECTION,
    normalizedSiteId,
    'sessions',
    visitorSessionId
  );
  const iso = new Date().toISOString();
  const logLine = `${iso}|${visitNote}`;

  void setDoc(
    sessionRef,
    {
      siteId: normalizedSiteId,
      slug: slug || '',
      lastSlug: slug || '',
      sourceDomain: sourceDomain || '',
      lastVisitNote: visitNote,
      lastVisitedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      visitLog: arrayUnion(logLine),
    },
    { merge: true }
  ).catch((err) => {
    console.error('[shortlinkMetrics] append visit log failed:', err);
  });
}

/** Update live session status fields without adding noisy rows to visitLog. */
export function updateShortlinkVisitorSessionStatus({
  siteId,
  slug,
  sourceDomain,
  sessionState,
  fullscreenState,
  fullscreenMode,
  fullscreenNative,
  fullscreenPseudo,
  fullscreenRequestStatus,
  fullscreenRequestError,
  fullscreenRequestAt,
  keyboardLockStatus,
  pageFocused,
  pageVisibility,
  viewportMatchesScreen,
  fullscreenExitMethod,
}) {
  const normalizedSiteId = (siteId || '').trim().toLowerCase();
  if (!normalizedSiteId) return;

  const visitorSessionId = getVisitorSessionId();
  if (!hasTrackedShortlinkVisitorSession(normalizedSiteId)) return;

  const payload = {
    siteId: normalizedSiteId,
    updatedAt: serverTimestamp(),
  };
  if (slug != null) {
    payload.slug = slug || '';
    payload.lastSlug = slug || '';
  }
  if (sourceDomain != null) payload.sourceDomain = sourceDomain || '';
  if (sessionState) {
    payload.sessionState = sessionState;
    payload.lastHeartbeatAt = serverTimestamp();
  }
  if (fullscreenState) {
    payload.fullscreenState = fullscreenState;
    payload.fullscreenUpdatedAt = serverTimestamp();
  }
  if (fullscreenMode != null) payload.fullscreenMode = fullscreenMode || '';
  if (fullscreenNative != null) payload.fullscreenNative = Boolean(fullscreenNative);
  if (fullscreenPseudo != null) payload.fullscreenPseudo = Boolean(fullscreenPseudo);
  if (fullscreenRequestStatus != null) {
    payload.fullscreenRequestStatus = fullscreenRequestStatus || '';
  }
  if (fullscreenRequestError != null) {
    payload.fullscreenRequestError = fullscreenRequestError || '';
  }
  if (fullscreenRequestAt) {
    payload.fullscreenRequestAt = new Date(fullscreenRequestAt);
  }
  if (keyboardLockStatus != null) payload.keyboardLockStatus = keyboardLockStatus || '';
  if (pageFocused != null) payload.pageFocused = Boolean(pageFocused);
  if (pageVisibility != null) payload.pageVisibility = pageVisibility || '';
  if (viewportMatchesScreen != null) {
    payload.viewportMatchesScreen = Boolean(viewportMatchesScreen);
  }
  if (fullscreenExitMethod) payload.fullscreenExitMethod = fullscreenExitMethod;

  void setDoc(
    doc(
      db,
      SHORTLINK_VISITORS_COLLECTION,
      normalizedSiteId,
      'sessions',
      visitorSessionId
    ),
    payload,
    { merge: true }
  ).catch((err) => {
    console.error('[shortlinkMetrics] session status update failed:', err);
  });
}
